import React from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard, FileText, Users, Sliders, Settings, LogOut, Plus, Shield,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_611a485b-e4e6-4486-b989-98a526add30a/artifacts/7n1cgjv3_ProCloud-Logo-Stacked-Primary.svg";

const navItems = [
  { to: "/dashboard", label: "Mission Control",  icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/quotes",    label: "Quotes",           icon: FileText,        testid: "nav-quotes" },
  { to: "/clients",   label: "Clients",          icon: Users,           testid: "nav-clients" },
  { to: "/pricing",   label: "Pricing Library",  icon: Sliders,         testid: "nav-pricing" },
  { to: "/settings",  label: "Settings",         icon: Settings,        testid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-base)" }}>
      {/* Sidebar */}
      <aside
        className="w-64 flex flex-col shrink-0 border-r"
        style={{ background: "var(--bg-app)", borderColor: "var(--border-subtle)" }}
        data-testid="app-sidebar"
      >
        <div className="px-6 pt-7 pb-6 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <Link to="/dashboard" className="block" data-testid="logo-link">
            <img src={LOGO_URL} alt="ProCloud" className="h-14 brightness-0 invert opacity-95" />
          </Link>
          <div className="mt-4 eyebrow-accent">Quote Engine</div>
        </div>

        <div className="px-4 pt-5">
          <button
            onClick={() => navigate("/quotes/new")}
            data-testid="sidebar-new-quote-btn"
            className="btn btn-primary w-full"
          >
            <Plus className="w-4 h-4" />
            New Quote
          </button>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="mb-3" data-testid="current-user">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                {user?.name || "Admin"}
              </div>
              {isSuper && (
                <div className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded"
                     style={{ background: "var(--gradient-accent)", color: "white" }}>
                  Super
                </div>
              )}
            </div>
            <div className="text-xs font-mono" style={{ color: "var(--text-faint)" }}>{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-xs tracking-widest uppercase font-bold transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
